import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Users, Briefcase, Target, DollarSign, TrendingUp, Activity, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MetricCard({ icon, label, value, color, prefix = '', suffix = '', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-[#0d0f17] border border-white/5 hover:border-[#22D3EE]/30 rounded-xl p-6 transition-all"
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
      <p className="text-sm text-[#94A3B8] mb-1">{label}</p>
      <p className="text-3xl font-bold text-[#F8FAFC]">
        {prefix}{value}{suffix}
      </p>
    </motion.div>
  );
}

export default function DashboardHome() {
  const { getAuthHeader } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/metrics`, { headers: getAuthHeader(), withCredentials: true }),
      axios.get(`${API}/admin/activities`, { headers: getAuthHeader(), withCredentials: true }),
    ])
      .then(([m, a]) => {
        setMetrics(m.data);
        setActivities(a.data);
      })
      .catch((e) => logger.error('Failed to load dashboard data:', e))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-[#22D3EE]" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div>
        <h1 className="text-3xl font-bold text-[#F8FAFC] mb-2">Dashboard</h1>
        <p className="text-[#94A3B8]">Visão geral do seu negócio em tempo real</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={<Target className="text-[#22D3EE]" size={24} />}
          label="Total de Leads"
          value={metrics?.total_leads || 0}
          color="bg-[#22D3EE]/10 border border-[#22D3EE]/30"
          delay={0}
        />
        <MetricCard
          icon={<TrendingUp className="text-emerald-400" size={24} />}
          label="Taxa de Conversão"
          value={metrics?.conversion_rate || 0}
          suffix="%"
          color="bg-emerald-500/10 border border-emerald-500/30"
          delay={0.1}
        />
        <MetricCard
          icon={<DollarSign className="text-[#4F46E5]" size={24} />}
          label="Ticket Médio"
          value={(metrics?.avg_ticket || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          prefix="R$ "
          color="bg-[#4F46E5]/10 border border-[#4F46E5]/30"
          delay={0.2}
        />
        <MetricCard
          icon={<DollarSign className="text-amber-400" size={24} />}
          label="Receita Total"
          value={(metrics?.total_revenue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          prefix="R$ "
          color="bg-amber-500/10 border border-amber-500/30"
          delay={0.3}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MetricCard
          icon={<Users className="text-[#22D3EE]" size={24} />}
          label="Clientes Ativos"
          value={metrics?.total_clients || 0}
          color="bg-[#22D3EE]/10 border border-[#22D3EE]/30"
          delay={0.4}
        />
        <MetricCard
          icon={<Briefcase className="text-[#4F46E5]" size={24} />}
          label="Projetos Totais"
          value={metrics?.total_projects || 0}
          color="bg-[#4F46E5]/10 border border-[#4F46E5]/30"
          delay={0.5}
        />
        <MetricCard
          icon={<Target className="text-emerald-400" size={24} />}
          label="Fechados / Perdidos"
          value={`${metrics?.leads_fechado || 0} / ${metrics?.leads_perdido || 0}`}
          color="bg-emerald-500/10 border border-emerald-500/30"
          delay={0.6}
        />
      </div>

      {/* Activities */}
      <div className="bg-[#0d0f17] border border-white/5 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <Activity className="text-[#22D3EE]" size={20} />
          <h2 className="text-xl font-semibold text-[#F8FAFC]">Atividades Recentes</h2>
        </div>
        {activities.length === 0 ? (
          <p className="text-[#94A3B8] text-center py-8">Nenhuma atividade ainda</p>
        ) : (
          <div className="space-y-3">
            {activities.slice(0, 10).map((activity) => (
              <div
                key={activity.id}
                data-testid={`activity-${activity.id}`}
                className="flex items-start gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="w-2 h-2 rounded-full bg-[#22D3EE] mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-[#F8FAFC] text-sm">{activity.description}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">
                    {new Date(activity.created_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
